/**
 * [TD-89 v2 Verdict 1.A] Dashboard league-tile kebab menu.
 *
 * Three-dot kebab top-right of each league-card. Click opens dropdown
 * with role/status-conditional actions:
 *   - Open League (always)
 *   - Delete League (commissioner + status not in SEASON_ACTIVE/PLAYOFFS/SEASON_COMPLETE)
 *   - Abandon Draft (commissioner + status === DRAFTING or PAUSED)
 *
 * NOTE: v1's Leave League item REMOVED in v2 (Capability C removed; manager
 * departures route through Capability E Convert Manager to CPU instead).
 *
 * `e.stopPropagation()` on every interactive element prevents the parent
 * tile-body click from firing.
 */
import { useEffect, useRef, useState } from "react";
import { MoreVertical, Trash2, OctagonX, ExternalLink } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { authHeaders } from "../../contexts/AuthContext";
import ConfirmModal from "../common/ConfirmModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLOCKED_DELETE = new Set(["SEASON_ACTIVE", "PLAYOFFS", "SEASON_COMPLETE"]);

export default function LeagueTileMenu({ league, isCommissioner, isDark, onOpen, onMutated }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // null | "delete" | "abandon"
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const canDelete = isCommissioner && !BLOCKED_DELETE.has(league.status);
  const canAbandon = isCommissioner && ["DRAFTING", "PAUSED"].includes(league.status);

  const stop = (e) => e.stopPropagation();

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/leagues/${league.id}`, { headers: authHeaders() });
      toast.success(`Deleted ${league.name}`);
      onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const handleAbandon = async () => {
    try {
      await axios.post(
        `${API}/commissioner/${league.id}/abandon-draft`,
        {},
        { headers: authHeaders() },
      );
      toast.success("Draft abandoned. Restart from the lobby when ready.");
      onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Abandon failed");
    }
  };

  const itemCls = `flex items-center gap-2 px-3 py-2 font-body text-sm w-full text-left transition-colors ${
    isDark ? "text-[#F5F5F5] hover:bg-white/[0.06]" : "text-[#0A0A0A] hover:bg-black/[0.05]"
  }`;
  const ddBg = isDark ? "bg-[#1C1C1C] border-[#252525]" : "bg-white border-black/[0.10]";

  return (
    <div ref={rootRef} className="absolute top-2 right-2 z-10" onClick={stop}>
      <button
        data-testid={`league-tile-menu-trigger-${league.id}`}
        onClick={(e) => { stop(e); setOpen((v) => !v); }}
        className={`p-1.5 rounded-md transition-colors ${
          isDark ? "text-[#A0A0A0] hover:text-[#FF6B00] hover:bg-white/[0.06]" : "text-[#666] hover:text-[#FF6B00] hover:bg-black/[0.05]"
        }`}
        aria-label="League actions"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div
          data-testid={`league-tile-menu-dropdown-${league.id}`}
          className={`absolute right-0 top-full mt-1 w-44 rounded-lg border shadow-xl overflow-hidden ${ddBg}`}
        >
          <button
            data-testid={`league-tile-menu-item-open-${league.id}`}
            onClick={(e) => { stop(e); setOpen(false); onOpen?.(); }}
            className={itemCls}
          >
            <ExternalLink size={14} /> Open League
          </button>
          {canAbandon && (
            <button
              data-testid={`league-tile-menu-item-abandon-${league.id}`}
              onClick={(e) => { stop(e); setOpen(false); setConfirm("abandon"); }}
              className={itemCls}
            >
              <OctagonX size={14} /> Abandon Draft
            </button>
          )}
          {canDelete && (
            <button
              data-testid={`league-tile-menu-item-delete-${league.id}`}
              onClick={(e) => { stop(e); setOpen(false); setConfirm("delete"); }}
              className={itemCls + " text-[#E03030]"}
            >
              <Trash2 size={14} /> Delete League
            </button>
          )}
        </div>
      )}
      <ConfirmModal
        open={confirm === "delete"}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => { await handleDelete(); setConfirm(null); }}
        title={`Delete ${league.name}?`}
        body={<>This permanently deletes the league and all its data. <strong>This cannot be undone.</strong></>}
        confirmLabel="Delete"
        destructive
        typedConfirmation="DELETE"
        isDark={isDark}
        testIdPrefix="league-tile-confirm-delete"
      />
      <ConfirmModal
        open={confirm === "abandon"}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => { await handleAbandon(); setConfirm(null); }}
        title="Abandon Draft?"
        body={<>All draft progress will be lost. Managers stay seated. You can restart the draft from the lobby.</>}
        confirmLabel="Abandon Draft"
        destructive
        isDark={isDark}
        testIdPrefix="league-tile-confirm-abandon"
      />
    </div>
  );
}
